import PropTypes from 'prop-types';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ReplyIcon from '@mui/icons-material/Reply';
import { Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import AttachDocumentContainer from '../../Messages/containers/AttachDocumentContainer';

import ModalHandlerComponent from '../../Modal/ModalHandlerComponent';

import { isAdmin } from '../../../utils';
import { checkMessage, getMessagesHeight } from '../../Messages/utils';

import {
  ACTIVE_THREAD_FILE,
  MESSAGES_SELECT_DOCUMENT,
  MESSAGES_UPLOAD_DOCUMENT,
} from '../../Messages/constants';

import DropDownMenu from '../../DropDownMenu';
import { CUSTOM } from '../../Modal/modalTypes';
import StyledTextField from '../../Shared/StyledTextField';

const ignoredProps = ['isMultiRecipients'];

const StyledSendButton = styled(IconButton, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, isMultiRecipients }) => ({
  height: '3rem',
  width: '4rem',
  margin: 0,
  padding: 0,
  backgroundColor: theme.palette.custom.primary2,
  borderRadius: '1rem',
  color: theme.palette.common.white,
  position: 'absolute',
  top: '0.5rem',
  right: '0.5rem',

  '&:hover': {
    opacity: '0.8',
    backgroundColor: theme.palette.custom.primary2,
  },

  ...(isMultiRecipients && {
    width: '9rem',
    fontSize: '1.4rem',
  }),
}));

const StyledAttachButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.custom.primary2,
  borderRadius: '1rem',
  color: theme.palette.common.white,
  width: '4rem',
  height: '4rem',
  marginRight: '1.5rem',
  padding: 0,

  '&:hover': {
    opacity: '0.8',
    backgroundColor: theme.palette.custom.primary2,
  },
}));

const StyledMainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.common.white,
  padding: '3rem',
  overflow: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: '1rem 2rem 1rem 1rem',
  },
}));

const StyledHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  marginBottom: '3rem',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '1rem',
  },
}));

const StyledTitle = styled(Typography)(() => ({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2.4rem',
}));

const ThreadTemplateComponent = ({
  arrowBackOnclick,
  attachModalType,
  attachmentDocumentId,
  attachmentDocumentName,
  characterLimit,
  checkNewAttachment,
  children,
  containerIds,
  confirmSendingMultiRecipients,
  inputValue,
  isArrowBackActive,
  isAttachIconShown,
  isInputDisabled,
  isMessagesContainerActive,
  handleNewUserMessage,
  recipientsCount,
  setAttachModalType,
  setDocumentAsAttachment,
  setInputValue,
  showInput,
  subtitleWithUrl,
  title,
  widgetHeader,
  widgetType,
}) => {
  const { t } = useTransl();
  const {
    containerId,
    containerTitleId,
    messagesContainerId,
    messagesInputId,
  } = containerIds;
  const subtitle = subtitleWithUrl ?? widgetHeader?.subtitle;
  const handleChange = (event) => {
    setInputValue(event.target.value);
  };
  const onSendMessage = () => {
    handleNewUserMessage();
    setInputValue('');
  };
  const isSendButtonDisabled =
    isInputDisabled || (checkMessage(inputValue) && !attachmentDocumentId);

  const isMultiRecipients = recipientsCount > 1;

  const onConfirmSendingMessage = () => {
    if (isSendButtonDisabled) return null;
    return isMultiRecipients
      ? confirmSendingMultiRecipients(recipientsCount, onSendMessage)
      : onSendMessage();
  };

  const messagesContainerHeight = getMessagesHeight();

  const modalProps = {
    modal: {
      modalType: CUSTOM,
      modalProps: {
        title: t('actions.sendDocument'),
      },
    },
    onModalClose: () => setAttachModalType(null),
  };

  const attachOptions = [
    {
      label: t('actions.attachFileFrom'),
      disabled: true,
      onClick: () => {},
    },
    {
      label: t('messages.yourComputer'),
      onClick: () => setAttachModalType(MESSAGES_UPLOAD_DOCUMENT),
    },
    {
      label: t('title.documentLibrary'),
      onClick: () => setAttachModalType(MESSAGES_SELECT_DOCUMENT),
    },
  ];

  const AttachmentDocumentComponent = (
    <Box
      display="flex"
      alignItems="center"
      ml="5.5rem"
      height="4rem"
      id={ACTIVE_THREAD_FILE}
    >
      {attachmentDocumentId && attachmentDocumentName ? (
        <>
          <Typography>{attachmentDocumentName}</Typography>
          <Box ml="1rem" />
          <IconButton
            onClick={() => setDocumentAsAttachment(null, null)}
            aria-label={t('actions.close')}
            size="large"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : null}
    </Box>
  );

  const AttachmentFileComponent =
    !isInputDisabled && isAttachIconShown ? (
      <DropDownMenu
        items={attachOptions}
        anchorOrigin={{
          vertical: -125,
          horizontal: 'left',
        }}
        CustomAnchor={({ onClick }) => (
          <StyledAttachButton
            onClick={onClick}
            aria-label={t('actions.attachFile')}
            size="large"
          >
            <AttachFileIcon
              sx={{ transform: 'rotate(45deg)' }}
              fontSize="large"
            />
          </StyledAttachButton>
        )}
      />
    ) : null;

  return (
    <>
      <StyledMainContainer id={containerId}>
        {Boolean(title) && (
          <StyledHeaderContainer id={containerTitleId}>
            <Box display="flex" gap="1rem" width="100%">
              {isArrowBackActive && (
                <IconButton
                  onClick={() => arrowBackOnclick()}
                  edge="end"
                  aria-label={t('actions.back')}
                  size="large"
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Box width="100%" gap="1rem" flexGrow={1}>
                <StyledTitle align="center">{title}</StyledTitle>
                <Typography textAlign="center" fontWeight="bold">
                  {subtitle}
                </Typography>
              </Box>
            </Box>
          </StyledHeaderContainer>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            marginRight: '-2rem',
          }}
          height={messagesContainerHeight}
          id={messagesContainerId}
        >
          {isMessagesContainerActive ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Typography variant="h5">
                {t('messages.noThreadSelected')}
              </Typography>
            </Box>
          ) : (
            children
          )}
        </Box>

        {AttachmentDocumentComponent}

        {showInput && !isAdmin() ? (
          <Box
            sx={{ display: 'flex', paddingBottom: '0', paddingTop: '2rem' }}
            id={messagesInputId}
          >
            {AttachmentFileComponent}
            <StyledTextField
              id="message_input"
              onChange={handleChange}
              multiline
              maxRows={3}
              disabled={isInputDisabled}
              value={inputValue}
              InputProps={{
                sx: [
                  { paddingRight: '5rem' },
                  isMultiRecipients && { paddingRight: '10rem' },
                ],
                endAdornment: (
                  <StyledSendButton
                    disabled={isSendButtonDisabled}
                    isMultiRecipients={isMultiRecipients}
                    onClick={onConfirmSendingMessage}
                    edge="end"
                    aria-label={t('actions.reply')}
                    size="large"
                  >
                    {isMultiRecipients ? (
                      t('actions.replyAll')
                    ) : (
                      <ReplyIcon sx={{ transform: 'scaleX(-1)' }} />
                    )}
                  </StyledSendButton>
                ),
                maxLength: characterLimit ?? Number.MAX_SAFE_INTEGER,
              }}
              FormHelperTextProps={{
                component: 'div',
              }}
              helperText={
                <Box
                  display="flex"
                  justifyContent="flex-end"
                >{`${inputValue.length}/${characterLimit}`}</Box>
              }
            />
          </Box>
        ) : null}
      </StyledMainContainer>
      {attachModalType && (
        <ModalHandlerComponent {...modalProps}>
          <AttachDocumentContainer
            closeModal={() => setAttachModalType(null)}
            type={attachModalType}
            checkNewAttachment={checkNewAttachment}
            handleNewUserMessage={handleNewUserMessage}
            widgetType={widgetType}
          />
        </ModalHandlerComponent>
      )}
    </>
  );
};

ThreadTemplateComponent.defaultProps = {
  attachModalType: null,
  attachmentDocumentId: null,
  attachmentDocumentName: null,
  isMultiRecipients: false,
  inputValue: null,
  containerIds: {
    containerId: 'active_thread_container',
    containerTitleId: 'active_thread_title',
    messagesContainerId: 'active_thread_messages',
    messagesInputId: 'active_thread_input',
    messagesFileId: 'active_thread_file',
  },
  confirmSendingMultiRecipients: () => {},
  subtitleWithUrl: null,
  title: null,
  recipientsCount: null,
  widgetHeader: null,
  widgetType: null,
};

ThreadTemplateComponent.propTypes = {
  attachmentDocumentId: PropTypes.number,
  attachmentDocumentName: PropTypes.string,
  arrowBackOnclick: PropTypes.func.isRequired,
  attachModalType: PropTypes.string,
  isArrowBackActive: PropTypes.bool.isRequired,
  isMessagesContainerActive: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  showInput: PropTypes.bool.isRequired,
  isInputDisabled: PropTypes.bool.isRequired,
  isMultiRecipients: PropTypes.bool,
  characterLimit: PropTypes.number.isRequired,
  inputValue: PropTypes.string,
  containerIds: PropTypes.shape({
    containerId: PropTypes.string,
    containerTitleId: PropTypes.string,
    messagesContainerId: PropTypes.string,
    messagesInputId: PropTypes.string,
    messagesFileId: PropTypes.string,
  }),
  handleNewUserMessage: PropTypes.func.isRequired,
  title: PropTypes.node,
  subtitleWithUrl: PropTypes.node,
  setInputValue: PropTypes.func.isRequired,
  setAttachModalType: PropTypes.func.isRequired,
  setDocumentAsAttachment: PropTypes.func.isRequired,
  isAttachIconShown: PropTypes.bool.isRequired,
  confirmSendingMultiRecipients: PropTypes.func,
  checkNewAttachment: PropTypes.func.isRequired,
  recipientsCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  widgetHeader: PropTypes.instanceOf(Object),
  widgetType: PropTypes.string,
};

export default ThreadTemplateComponent;
